import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, Loader2 } from 'lucide-react';
import { downloadUrl } from '@/lib/apiClient';
import type { LibraryFile } from '@/lib/types';
import { Markdown } from '@/components/Markdown';

type Kind = 'markdown' | 'text' | 'pdf' | 'image' | 'other';

function kindOf(file: LibraryFile): Kind {
  const ct = (file.content_type || '').toLowerCase();
  const name = (file.filename || '').toLowerCase();
  if (ct.includes('markdown') || name.endsWith('.md')) return 'markdown';
  if (ct === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return 'image';
  if (ct.startsWith('text/') || name.endsWith('.txt')) return 'text';
  return 'other';
}

export function FilePreviewModal({ file, onClose }: { file: LibraryFile; onClose: () => void }) {
  const kind = kindOf(file);
  const dl = downloadUrl(`/library/files/${file.id}/download`);
  const inlineUrl = `${dl}&inline=1`;

  const isTextual = kind === 'markdown' || kind === 'text';
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(isTextual);

  // Close on Escape + lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const mainEl = document.querySelector('main');
    const prevMain = mainEl ? mainEl.style.overflow : '';
    const prevBody = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    if (mainEl) {
      mainEl.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevBody;
      if (mainEl) {
        mainEl.style.overflow = prevMain;
      }
    };
  }, [onClose]);

  // Fetch text/markdown content for inline rendering.
  useEffect(() => {
    if (!isTextual) return;
    let alive = true;
    setLoading(true);
    setError(null);
    fetch(dl)
      .then((r) => {
        if (!r.ok) throw new Error('load failed');
        return r.text();
      })
      .then((t) => alive && (setText(t), setLoading(false)))
      .catch(() => alive && (setError('Could not load this file for preview.'), setLoading(false)));
    return () => {
      alive = false;
    };
  }, [dl, isTextual]);

  let body: ReactNode;
  if (kind === 'pdf') {
    body = <iframe title={file.title} src={inlineUrl} className="h-[78vh] w-full bg-white" />;
  } else if (kind === 'image') {
    body = (
      <div className="grid place-items-center p-4">
        <img src={inlineUrl} alt={file.title} className="max-h-[78vh] max-w-full rounded-md" />
      </div>
    );
  } else if (kind === 'other') {
    body = (
      <div className="p-12 text-center text-muted-foreground">
        <FileText className="mx-auto mb-3 h-8 w-8" />
        <p>No inline preview for this file type.</p>
        <a
          href={dl}
          download
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
        >
          <Download className="h-4 w-4" /> Download to view
        </a>
      </div>
    );
  } else if (loading) {
    body = (
      <div className="flex items-center justify-center gap-2 p-16 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
      </div>
    );
  } else if (error) {
    body = <div className="p-12 text-center text-destructive">{error}</div>;
  } else if (kind === 'markdown') {
    body = (
      <div className="px-6 py-5">
        <Markdown>{text || ''}</Markdown>
      </div>
    );
  } else {
    body = <pre className="whitespace-pre-wrap px-6 py-5 text-sm text-foreground">{text}</pre>;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center gap-3 border-b border-border px-5 py-3.5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h3 className="truncate font-semibold text-foreground">{file.title}</h3>
            <p className="truncate font-mono text-xs text-muted-foreground">
              {file.filename} · {Math.max(1, Math.round(file.byte_size / 1024))} KB
            </p>
          </div>
          <a
            href={dl}
            download
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm text-foreground hover:bg-accent transition-colors"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Download</span>
          </a>
          <button
            onClick={onClose}
            aria-label="Close preview"
            className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">{body}</div>
      </div>
    </div>,
    document.body
  );
}
